// Shared karma <-> INR conversion for all user-facing displays.
//
// LOCKED RATE: 1 rupee = 10 karma  =>  rupees = round(karma / 10).
// Examples: Express 3-min = 450 karma = Rs.45; Detailed 750 = Rs.75;
//           Comprehensive 2250 = Rs.225; Life Guidance 2500 = Rs.250.
//
// Bonus/promo karma is marketing only and never changes this display rate.
// Never hardcode a different rate anywhere -- import these helpers instead.

export const KARMA_PER_RUPEE = 10;

// Session rate: 150 Karma per minute (backend SESSION_KARMA_PER_MINUTE,
// routes/session.py; Express 3 min = 450). Minutes shown for a balance are
// always derived from Karma here — never from the legacy `wallet_minutes`
// field, which is Karma / 100 and overstates time by half (8,005 Karma is
// 53 min, not 80).
export const KARMA_PER_MINUTE = 150;

/** Whole session minutes a Karma balance buys, e.g. 8005 -> 53. */
export function karmaToMinutes(karma) {
  const n = Number(karma) || 0;
  return n > 0 ? Math.floor(n / KARMA_PER_MINUTE) : 0;
}

/**
 * Convert a karma amount to its rupee equivalent (rounded).
 * @param {number} karma
 * @returns {number} rupees
 */
export function karmaToInr(karma) {
  const n = Number(karma) || 0;
  return Math.round(n / KARMA_PER_RUPEE);
}

/**
 * Rupee string for a karma amount, e.g. 450 -> "₹45".
 */
export function formatInr(karma) {
  return `₹${karmaToInr(karma).toLocaleString('en-IN')}`;
}

/**
 * Karma string with thousands separators, e.g. 2250 -> "2,250 karma".
 * @param {number} karma
 * @param {object} [opts]
 * @param {boolean} [opts.withLabel=true] append the " karma" label
 */
export function formatKarma(karma, { withLabel = true } = {}) {
  const n = Number(karma) || 0;
  const num = n.toLocaleString('en-IN');
  return withLabel ? `${num} karma` : num;
}

/**
 * Canonical combined display: karma primary, rupees in parentheses.
 * e.g. 450 -> "450 karma (₹45)". Use this everywhere a karma cost/balance
 * is shown to the user (session start, wallet, paywall, summary, top-up).
 */
export function formatKarmaWithInr(karma) {
  return `${formatKarma(karma)} (${formatInr(karma)})`;
}

/**
 * Coarse balance bucket for analytics (never a raw balance, so PII-free and
 * low-cardinality). Boundaries follow the session plan costs — 450 (3-min),
 * 750 (5-min), 2250 (15-min) — so a bucket answers "how many plans can this
 * balance cover", which is what the entry funnel actually cares about.
 *   zero      → 0
 *   under_min → 1..449   (cannot afford any session)
 *   one_plan  → 450..749 (covers the 3-min only — the trial 599 lands here)
 *   two_plan  → 750..2249
 *   all_plans → 2250+
 * @param {number} karma
 * @returns {string}
 */
export function karmaBalanceBucket(karma) {
  const n = Number(karma) || 0;
  if (n <= 0) return 'zero';
  if (n < 450) return 'under_min';
  if (n < 750) return 'one_plan';
  if (n < 2250) return 'two_plan';
  return 'all_plans';
}
